/* @jsxImportSource @emotion/react */

import React from 'react'
import { css } from '@emotion/react'
import classNames from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'

import { ArrowRightIcon } from '@slite/icons'

import {
  type CustomerQuote,
  getCustomerQuote,
} from '../../components/customerQuotes'
import { Section } from '../../components/Section'
import type { SliteBackgroundColor } from '../../components/SlitePage'
import { WebsiteText } from '../../components/WebsiteText'
import { useRoutes } from '../../routes'
import { SMALL_BREAKPOINT } from '../../utils/breakpoints'

const getCaseStudiesContent = (routes: ReturnType<typeof useRoutes>) => {
  return [
    {
      title: 'Leveraging AI for growth',
      description: 'Switched from Tettra & Google Docs',
      author: 'Sebastien Gendreau',
      authorLabel: 'Head of Product at Agora Pulse',
      companyLogoSrc:
        'https://cdn.sanity.io/images/dnsgqii5/production/0bf8633a236c6cac7713394c509e0db9c1a12590-225x225.png',
      href: routes.customers.detail({ slug: 'agorapulse' }),
    },
    {
      title: 'Scaling global teams',
      description: 'Switched from five tools',
      author: 'Martijn Hazelaar',
      authorLabel: 'Digital Manager at Vanmoof',
      companyLogoSrc:
        'https://cdn.sanity.io/images/dnsgqii5/production/1233da015c3f54d4b39398cf23bff77788db43db-225x225.png',
      href: routes.customers.detail({ slug: 'vanmoof' }),
    },
  ]
}

const getTestimonialsMetricsContent = () => {
  return [
    {
      metric: '75% faster',
      description: 'team onboarding',
      index: 0,
      className: '',
    },
    {
      metric: '3x more',
      description: 'productive team meeting',
      index: 3,
      className: '',
    },
    {
      metric: '90% fewer',
      description: 'repeated questions',
      className: 'display-none-small',
      index: 6,
    },
  ]
}

const MetricCard = ({
  metric,
  description,
}: {
  metric: string
  description: string
}) => (
  <div
    className="grid-span-4 grid-span-12-small flexbox flexbox-centered flexbox-column background-shade-100 padding-40 radius-larger"
    css={css`
      @media (max-width: ${SMALL_BREAKPOINT}px) {
        padding: 24px;
      }
    `}
  >
    <div className="text-align-center">
      <p className="font-family-title font-size-2 font-size-3-small margin-bottom-2-small font-weight-medium margin-bottom-8">
        {metric}
      </p>
      <p className="font-size-6 shade-20">{description}</p>
    </div>
  </div>
)

type TestimonialItem = {
  type: 'testimonial'
  testimonial: string
  name: string
  title: string
  className: string
  pictureUrl: string
}

type MetricItem = {
  type: 'metric'
  metric: string
  description: string
  index: number
  className: string
}

export const SectionTestimonials = ({
  testimonials,
  withCaseStudies = true,
  background = 'moon-dust',
}: {
  testimonials?: Omit<TestimonialItem, 'type'>[]
  withCaseStudies?: boolean
  background?: SliteBackgroundColor
}) => {
  const intl = useIntl()
  const routes = useRoutes()
  const caseStudiesContent = getCaseStudiesContent(routes)
  const testimonialsMetricsContent = getTestimonialsMetricsContent()
  const metricsIndexes = [0, 3, 6]

  const testimonialQuotes: (CustomerQuote & { className: string })[] =
    testimonials ?? [
      {
        ...getCustomerQuote('DanielWundertaxAdoption', intl),
        className: 'grid-span-8',
      },
      {
        ...getCustomerQuote('MiguelPVCaseDocumentation', intl),
        className: 'grid-span-4',
      },
      {
        ...getCustomerQuote('JosselinMeeroGrowth', intl),
        className: 'grid-span-4',
      },
      {
        ...getCustomerQuote('ThomasPremiumPlusKnowledge', intl),
        className: 'grid-span-8',
      },
    ]

  const combinedContent: (MetricItem | TestimonialItem)[] = []
  let testimonialIndex = 0

  for (let i = 0; i < 7; i++) {
    if (metricsIndexes.includes(i)) {
      const metric = testimonialsMetricsContent.find((m) => m.index === i)
      if (metric) {
        combinedContent.push({ type: 'metric', ...metric })
      }
    } else if (testimonialIndex < testimonialQuotes.length) {
      combinedContent.push({
        type: 'testimonial',
        ...testimonialQuotes[testimonialIndex],
      })
      testimonialIndex++
    }
  }

  return (
    <Section background={background} containerTopPadding={140}>
      <WebsiteText
        variant="title"
        className="margin-bottom-40 text-align-center padding-bottom-16"
      >
        <FormattedMessage
          id="sliteWebsite.index.testimonials.section.title"
          defaultMessage={'Trusted by teams like yours'}
        />
      </WebsiteText>
      <div
        className="grid-12 grid-gap-24 grid-gap-12-small"
        css={css`
          margin-bottom: 88px;

          @media (min-width: ${SMALL_BREAKPOINT}px) {
            grid-auto-rows: 1fr;
          }
        `}
      >
        {combinedContent.map((item, index) => {
          if (item.type === 'metric') {
            return (
              <MetricCard
                key={`metric-${index}`}
                metric={item.metric}
                description={item.description}
              />
            )
          }

          return (
            <div
              key={`testimonial-${index}`}
              className={classNames(
                'grid-span-12-small background-moon-dust-lighter padding-40 radius-larger height-100 flexbox flexbox-column flexbox-justify-between padding-24-small flexbox-justify-center',
                item.className
              )}
              css={css`
                @media (max-width: ${SMALL_BREAKPOINT}px) {
                  padding: 24px;
                }
              `}
            >
              <p className="margin-bottom-16">{item.testimonial}</p>
              <div className="flexbox flexbox-align-center flexbox-column-gap-12">
                <img
                  src={item.pictureUrl}
                  className="radius-round"
                  alt=""
                  css={css`
                    width: 38px;
                  `}
                />
                <div>
                  <p className="font-size-7 shade-05 line-height-normal margin-bottom-4">
                    {item.name}
                  </p>
                  <p className="font-size-8 shade-20 line-height-normal">
                    {item.title}
                  </p>
                </div>
              </div>
            </div>
          )
        })}
      </div>
      {withCaseStudies && (
        <div>
          <WebsiteText
            variant="title"
            className="margin-bottom-40 text-align-center"
          >
            <FormattedMessage
              id="sliteWebsite.testimonials.caseStudy.title"
              defaultMessage="The Slite edge, in our customer words"
            />
          </WebsiteText>
          <div className="flexbox flexbox-column flexbox-row-gap-20">
            {caseStudiesContent.map((caseStudy) => (
              <a
                href={caseStudy.href}
                key={caseStudy.title}
                className="display-block padding-vertical-32 background-shade-100 padding-horizontal-40 grid-12 pointer shade-05"
                css={css`
                  position: relative;
                  border-radius: 16px;
                  &:before {
                    position: absolute;
                    content: ' ';
                    width: calc(100% + 16px);
                    height: calc(100% + 16px);
                    border: 1px solid rgba(0, 0, 0, 0);
                    border-radius: 24px;
                    top: -8px;
                    left: -8px;
                  }

                  &:hover:before {
                    border: 1px solid var(--shade-00);
                  }
                `}
              >
                <div className="grid-span-5 grid-span-12-small margin-bottom-8-small">
                  <div className="flexbox flexbox-align-center flexbox-column-gap-20 flexbox-column-small flexbox-align-start-small">
                    <img
                      className="radius-round position-relative margin-bottom-12-small"
                      css={css`
                        display: block;
                        width: 48px;

                        &:before {
                          display: block;
                          position: absolute;
                          content: '';
                          width: 56px;
                          height: 56px;
                          top: -8px;
                          left: 0;
                          border: 1px solid var(--shade-00);
                        }
                      `}
                      src={caseStudy.companyLogoSrc}
                      alt=""
                    />
                    <div>
                      <p className="font-family-title font-size-4 margin-bottom-4 font-weight-medium flexbox-no-shrink">
                        {caseStudy.title}
                      </p>
                      <WebsiteText
                        variant="smallUppercase"
                        className="shade-30"
                      >
                        {caseStudy.description}
                      </WebsiteText>
                    </div>
                  </div>
                </div>
                <div className="grid-span-5 margin-top-4 grid-span-12-small margin-bottom-16-small">
                  <p className="margin-bottom-4 font-weight-semibold">
                    {caseStudy.author}
                  </p>
                  <p className="font-size-7 shade-20">
                    {caseStudy.authorLabel}
                  </p>
                </div>
                <div className="grid-span-2 flexbox flexbox-align-center flexbox-justify-end flexbox-column-gap-4 grid-span-12-small flexbox-justify-start-small">
                  <span className="font-family-title font-size-4 font-weight-medium">
                    <FormattedMessage
                      id="sliteWebsite.index.testimonials.caseStudy.title"
                      defaultMessage="Case study"
                    />
                  </span>
                  <ArrowRightIcon
                    size={22}
                    css={css`
                      transform: rotate(-45deg);
                    `}
                  />
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </Section>
  )
}
